import React, { useState } from "react";
import {
  ClipboardList,
  ChevronUp,
  ChevronDown,
  Copy,
  Check,
  Loader2,
} from "lucide-react";

import item1 from "../Assets/Food.svg";

const formatAmount = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function OrderSummary({
  isDelivered,
  orderId,
  products = [],
  pricing = {},
  merchantStatus,
  onFetchOrderDetails = null,
  loading = false,
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const handleCopy = async () => {
    if (!orderId) return;

    try {
      await navigator.clipboard.writeText(String(orderId));
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch (error) {
      console.error("Order ID copy error:", error);
    }
  };

  const handleToggle = async () => {
    const nextOpenState = !open;
    setOpen(nextOpenState);

   if (nextOpenState && !loaded && typeof onFetchOrderDetails === "function") {
     try {
       const result = await onFetchOrderDetails();

       if (result) {
         setLoaded(true);
       }
     } catch (error) {
       console.error("Order details fetch error:", error);
     }
   }
  };

  const paymentMode = pricing?.paymentMode || "Online";

  const orderCompleted =
    isDelivered ||
    merchantStatus === "successful" ||
    merchantStatus === "delivered";

  return (
    <div className="mx-4 mt-4 mb-6 max-w-md space-y-4 rounded-xl bg-white p-4 shadow">
      <div className="flex items-start gap-4">
        <div className="rounded-full bg-gray-100 p-3">
          <ClipboardList size={18} />
        </div>

        <div className="min-w-0 flex-1">
          <p className="font-semibold">Order Summary</p>

          <div className="mt-1 flex items-center gap-2">
            <p className="truncate text-sm text-gray-500">
              Order ID: {orderId || "-"}
            </p>

            <button
              type="button"
              onClick={handleCopy}
              disabled={!orderId}
              className="rounded p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Copy order ID"
            >
              {copied ? (
                <Check size={16} className="text-orange-600" />
              ) : (
                <Copy size={16} className="text-gray-600" />
              )}
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={handleToggle}
          className="rounded-full p-2 hover:bg-gray-100"
          aria-label={open ? "Close order summary" : "Open order summary"}
        >
          {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      {open && (
        <div className="space-y-4">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-5 text-sm text-gray-500">
              <Loader2 size={18} className="animate-spin" />
              Loading order details...
            </div>
          )}

          {!loading && (
            <>
              {products.length > 0 ? (
                <>
                  <div className="space-y-3">
                    {products.map((item, index) => (
                      <div
                        key={item?.id || `${item?.name}-${index}`}
                        className="flex items-center gap-3"
                      >
                        <img
                          src={
                            typeof item?.image === "string" && item.image.trim()
                              ? item.image
                              : item1
                          }
                          alt={item?.name || "Order item"}
                          className="h-14 w-14 rounded-lg border object-cover"
                          onError={(event) => {
                            event.currentTarget.src = item1;
                          }}
                        />

                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">
                            {item?.name || "Item"}
                          </p>

                          <p className="text-xs text-gray-500">
                            Qty: {Number(item?.qty || 0)} × ₹
                            {formatAmount(item?.unitPrice)}
                          </p>
                        </div>

                        <p className="whitespace-nowrap text-sm font-semibold">
                          ₹{formatAmount(item?.itemTotal)}
                        </p>
                      </div>
                    ))}
                  </div>

                  <hr />
                </>
              ) : (
                <p className="py-3 text-center text-xs text-gray-400">
                  Item details are not available
                </p>
              )}

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Item total</span>
                  <span>₹{formatAmount(pricing?.subTotal)}</span>
                </div>

                <div className="flex justify-between">
                  <span>CGST</span>
                  <span>₹{formatAmount(pricing?.cgst)}</span>
                </div>

                <div className="flex justify-between">
                  <span>SGST</span>
                  <span>₹{formatAmount(pricing?.sgst)}</span>
                </div>

                <div className="flex justify-between">
                  <span>Delivery charge</span>

                  {Number(pricing?.deliveryFee || 0) > 0 ? (
                    <span>₹{formatAmount(pricing?.deliveryFee)}</span>
                  ) : (
                    <span className="text-orange-600">FREE</span>
                  )}
                </div>

                <hr />

                <div className="flex justify-between font-semibold">
                  <span>Paid via {paymentMode}</span>
                  <span>₹{formatAmount(pricing?.total)}</span>
                </div>
              </div>

              <hr />

              <div className="text-sm font-medium text-orange-600">
                Payment via {paymentMode} •{" "}
                {orderCompleted ? "Order delivered" : "Order confirmed"}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}